import { FC, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { HiArrowLeft } from 'react-icons/hi';

import { isRTL } from 'src/utils/global';
import classes from './styles.module.css';

interface EmptyElementProps {
    /** Small, neutral glyph. Keep it simple — this is not an illustration slot. */
    icon?: ReactNode;
    title: string;
    msg?: string;
    /**
     * Optional single action. Provide `actionTo` for navigation or `onAction`
     * for an in-page action (clearing filters, retrying). Omit both when no
     * action would genuinely help — an empty state should never invent one.
     */
    actionLabel?: string;
    actionTo?: string;
    onAction?: () => void;
    /**
     * `page` fills a route's content area; `inline` sits inside an existing
     * panel or section and drops the card chrome so cards never nest.
     */
    variant?: 'page' | 'inline';
    /**
     * Announce the state politely. Worth it when this replaces a loading state
     * or a list the user just emptied (cart, wishlist, filtered results).
     * Off for `inline` by default: those sit on transient or secondary
     * surfaces — the categories dropdown would re-announce on every open.
     */
    announce?: boolean;
}

const EmptyElement: FC<EmptyElementProps> = ({
    icon,
    title,
    msg,
    actionLabel,
    actionTo,
    onAction,
    variant = 'page',
    announce = variant === 'page',
}) => {
    const showAction = Boolean(actionLabel && (actionTo || onAction));

    return (
        <div
            className={`${classes.wrapper} ${variant === 'inline' ? classes.inline : classes.page}`}
            role={announce ? 'status' : undefined}
        >
            {icon ? (
                <div className={classes.iconWrap} aria-hidden="true">
                    <span className={classes.icon}>{icon}</span>
                </div>
            ) : null}

            <h3 className={classes.title}>{title}</h3>
            {msg ? <p className={classes.message}>{msg}</p> : null}

            {showAction ? (
                <div className={classes.actions}>
                    {actionTo ? (
                        <Link to={actionTo} className={`btn__primary ${classes.cta}`}>
                            <HiArrowLeft
                                aria-hidden="true"
                                style={isRTL() ? { transform: 'scaleX(-1)' } : undefined}
                            />
                            {actionLabel}
                        </Link>
                    ) : (
                        <button type="button" onClick={onAction} className={`btn__primary ${classes.cta}`}>
                            {actionLabel}
                        </button>
                    )}
                </div>
            ) : null}
        </div>
    );
};

export default EmptyElement;
